import { useSyncExternalStore } from "react";

/** Langue de l'interface (décision d'Alexis du 30/09/2026 : interface en anglais). Le texte français sert de clé :
 *  `t("Données initiales")` rend « Initial data » en anglais et le texte tel quel en français ; une clé sans
 *  traduction reste en français (signalée dans la console en développement). L'anglais suit la terminologie de
 *  l'ordre en anglais : « Tout OTAN », orthographe britannique (manoeuvre, defence, armoured, centre) avec -ize
 *  (organize, neutralize) — voir docs/13-anglais-interface.md.
 *
 *  Le dictionnaire anglais n'est chargé que pour l'anglais (`loadLang`, avant le premier affichage et avant tout
 *  changement de langue). Changer de langue remonte l'application (main.tsx) : chaque texte est relu. Les
 *  messages du serveur, toujours en français, passent par `tServer` (traduction exacte ou modèle à trous). */

export type Lang = "fr" | "en";

export const LANGS: { key: Lang; label: string; name: string }[] = [
  { key: "fr", label: "FR", name: "Français" },
  { key: "en", label: "EN", name: "English" },
];

/** Interrupteur de langue montré aux utilisateurs (traduction livrée le 30/09/2026). À repasser à faux pour
 *  retirer l'anglais sans toucher au reste : l'interface revient alors au français pour tous. */
export const ENGLISH_READY = true;

const STORAGE_KEY = "generex:langue";
/** Cookie de la langue de l'interface (docs/13 « Langue des documents ») : lu par le serveur pour ce qui part sans
 *  l'en-tête `X-Generex-Langue` (téléchargement par lien, image d'une carte). Lisible par la page, tout le site. */
export const LANGUAGE_COOKIE = "generex_langue";
const listeners = new Set<() => void>();
/** Écouteurs d'un changement de langue (main.tsx : documents relus dans la nouvelle langue). */
const changeListeners = new Set<(next: Lang) => void>();
type Dictionary = Record<string, string>;
let en: Dictionary | null = null;
let server: Dictionary = {};
let patterns: [RegExp, string][] | null = null;

function stored(): Lang | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "fr" || value === "en" ? value : null;
  } catch {
    return null; // stockage refusé par le navigateur
  }
}

/** Langue au démarrage : celle choisie sur ce poste, sinon celle du navigateur (anglais s'il la demande avant le
 *  français), sinon le français. */
export function initialLang(): Lang {
  if (!ENGLISH_READY || typeof window === "undefined") return "fr";
  const saved = stored();
  if (saved) return saved;
  const wanted = (navigator.languages?.length ? navigator.languages : [navigator.language]).find((l) => /^(fr|en)\b/i.test(l));
  return wanted?.toLowerCase().startsWith("en") ? "en" : "fr";
}

let current: Lang = "fr";

export function lang(): Lang {
  return current;
}

/** Cookie `generex_langue` posé pour la langue `next` (un an, chemin /, SameSite=Lax). */
export function writeLanguageCookie(next: Lang): void {
  if (typeof document === "undefined") return;
  try {
    document.cookie = `${LANGUAGE_COOKIE}=${next}; path=/; max-age=31536000; SameSite=Lax`;
  } catch {
    // cookies refusés : l'en-tête des requêtes suffit
  }
}

/** Changement de langue (après l'adoption de la nouvelle) : rend la fonction qui retire l'écouteur. */
export function onLanguageChange(listener: (next: Lang) => void): () => void {
  changeListeners.add(listener);
  return () => changeListeners.delete(listener);
}

/** Charge le dictionnaire de la langue (l'anglais, à la demande) puis l'adopte ; le cookie de la langue suit. */
export async function loadLang(next: Lang): Promise<void> {
  if (next === "en" && !en) {
    const module = await import("./en");
    en = module.EN;
    server = module.SERVER;
    patterns = null;
  }
  writeLanguageCookie(next);
  if (next === current) return;
  current = next;
  if (typeof document !== "undefined") document.documentElement.lang = next;
  listeners.forEach((listener) => listener());
  changeListeners.forEach((listener) => listener(next));
}

/** Choix de l'utilisateur : appliqué aussitôt, puis retenu sur ce poste. Retenu seulement une fois la langue chargée
 *  (audit de persistance m14) : un dictionnaire introuvable (onglet ouvert pendant une mise en ligne) laisse la
 *  langue et la préférence inchangées, l'appelant le signale. */
export async function setLang(next: Lang): Promise<void> {
  await loadLang(next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // stockage refusé : le choix vaut pour cette ouverture
  }
}

export function useLang(): Lang {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => current,
  );
}

/** Tests : dictionnaire anglais fourni directement. */
export function setDictionaryForTests(dictionary: Dictionary, serverMessages: Dictionary = {}, next: Lang = "en"): void {
  en = dictionary;
  server = serverMessages;
  patterns = null;
  current = next;
}

const reported = new Set<string>();

type Params = Record<string, string | number | null | undefined>;

function fill(text: string, params?: Params): string {
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (match, name: string) => (name in params ? String(params[name] ?? "") : match));
}

/** Texte de l'interface dans la langue choisie ; `{nom}` reçoit `params.nom`. La clé est le texte français exact
 *  (apostrophes, espaces insécables et guillemets compris). */
export function t(fr: string, params?: Params): string {
  if (current === "en") {
    const text = en?.[fr];
    if (text !== undefined) return fill(text, params);
    if (import.meta.env.DEV && !reported.has(fr)) {
      reported.add(fr);
      console.warn(`[langue] traduction anglaise manquante : « ${fr} »`);
    }
  }
  return fill(fr, params);
}

/** Même texte français, sens différents selon l'endroit (« Annuler » : Cancel dans une fenêtre, Undo après une
 *  action) : `tc("défaire", "Annuler")` rend « Annuler » en français et cherche la clé « défaire|Annuler » en
 *  anglais (à défaut, la traduction ordinaire de « Annuler »). */
export function tc(context: string, fr: string, params?: Params): string {
  if (current === "en") {
    const text = en?.[`${context}|${fr}`];
    if (text !== undefined) return fill(text, params);
  }
  return t(fr, params);
}

/** Clé à traduire à l'affichage, pour les textes définis hors rendu (listes de vues, d'options…) : `k("Tous")`
 *  rend la clé française telle quelle ; l'affichage appelle `t(item.label)`. Le contrôle (scripts/i18n-check.mjs)
 *  vérifie que la clé est au dictionnaire. */
export function k(fr: string): string {
  return fr;
}

/** Paramètres régionaux des dates et des nombres. */
export function locale(): string {
  return current === "en" ? "en-GB" : "fr-FR";
}

/** Nom d'un pont dans la langue de l'interface, comme le serveur (`exports.model.bridge_name`) : en anglais,
 *  « Pont sans nom » (cartes de l'iPad) et « Pont » seul deviennent « Unnamed bridge » et « Bridge » ; sur une zone
 *  libre (`zone`), le nom composé par Generex (« Pont » + route) devient « <route> bridge ». Les noms propres des
 *  ponts d'un camp (« Pont Neuf ») restent tels quels. */
export function bridgeName(name: string | null | undefined, zone = false): string {
  const text = (name ?? "").split(/\s+/).filter(Boolean).join(" ");
  if (current !== "en") return text || "Pont";
  if (!text || text === "Pont") return "Bridge";
  if (text.startsWith("Pont sans nom")) return "Unnamed bridge";
  if (zone && text.startsWith("Pont ")) return `${text.slice(5)} bridge`;
  return text;
}

const SIZE_UNITS_EN: Record<string, string> = { o: "B", Ko: "KB", Mo: "MB", Go: "GB", To: "TB" };
/** Trous de modèle qui portent un message du serveur (motif de refus, source des données), traduit à son tour par
 *  `tServer` ; les autres (noms d'unités, de camps, désignations saisies) restent tels quels. */
const NESTED_HOLES = new Set(["reason", "motif", "source"]);
/** Trous qui portent un échelon nommé par le serveur (« compagnie », « bataillon, régiment ») : chaque libellé est
 *  traduit par le dictionnaire (messages des ordres de bataille construits). */
const ECHELON_HOLE = /echelon|expected|allowed/;
/** Dossiers d'autres comptes comptés au bout d'une liste de noms (comptes nominatifs, docs/17 §3.9 : « « A » et 2
 *  dossier(s) d'autres comptes ») : la partie comptée est traduite, les noms restent tels quels. */
const OTHER_ACCOUNTS = /^([\s\S]*?)(?: et )?(\d+) dossier\(s\) d'autres comptes$/;
const OTHER_ACCOUNTS_KEY = "{n} dossier(s) d'autres comptes";
/** Nombre dans la langue de l'interface (`toLocaleString`), séparateur des milliers français en espace insécable
 *  ordinaire : l'espace fine insécable que rend `fr-FR` est presque invisible dans la police de l'interface
 *  (« 13977 L » au lieu de « 13 977 L »). */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return value.toLocaleString(locale(), options).replace(/\u202f/g, "\u00a0");
}

function compile(key: string): RegExp {
  const source = key
    .split(/(\{\w+\})/)
    .map((part) => {
      const hole = /^\{(\w+)\}$/.exec(part);
      return hole ? `(?<${hole[1]}>[\\s\\S]+?)` : part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    })
    .join("");
  return new RegExp(`^${source}$`);
}

/** Message du serveur (rédigé en français) dans la langue de l'interface : traduction exacte, sinon modèle à trous
 *  (« Camp inconnu sur ce serveur : {camp}. »), sinon le message d'origine. */
export function tServer(message: string): string {
  if (current !== "en" || !message) return message;
  const exact = server[message] ?? en?.[message];
  if (exact !== undefined) return exact;
  patterns ??= Object.entries(server)
    .filter(([key]) => /\{\w+\}/.test(key))
    .map(([key, text]) => [compile(key), text]);
  for (const [pattern, text] of patterns) {
    const found = pattern.exec(message);
    if (found) {
      return text.replace(/\{(\w+)\}/g, (match, name: string) => {
        const value = found.groups?.[name];
        if (value === undefined) return match;
        if (ECHELON_HOLE.test(name))
          return value
            .split(", ")
            .map((part) => en?.[part] ?? part)
            .join(", ");
        // Motif lui-même message du serveur (« Zone non recréée : {reason} ») : traduit à son tour, y compris quand
        // le serveur l'a clos d'un point (« Longueur admise : 5 à 200 km. ») ou qu'il a ses propres trous
        // (« Nombre de zones atteint pour cette licence (20 au plus) : … »). Seulement pour les trous de motif
        // (`NESTED_HOLES`) : un nom saisi (« PC », « Ponts ») qui coïncide avec une clé reste tel quel.
        if (NESTED_HOLES.has(name) && server[value] !== undefined) return server[value]!;
        if (NESTED_HOLES.has(name) && value !== message && (value.endsWith(".") || value.includes(" : "))) {
          const nested = tServer(value);
          if (nested !== value) return nested;
          if (value.endsWith(".")) {
            const bare = tServer(value.slice(0, -1));
            if (bare !== value.slice(0, -1)) return `${bare.replace(/\.$/, "")}.`;
          }
        }
        // Taille en unités françaises (espace de stockage) : « 4,9 Go » → « 4.9 GB ».
        const size = /^(-?\d+(?:,\d+)?) (o|Ko|Mo|Go|To)$/.exec(value);
        if (size) return `${size[1]!.replace(",", ".")} ${SIZE_UNITS_EN[size[2]!]}`;
        const others = OTHER_ACCOUNTS.exec(value);
        const counted = server[OTHER_ACCOUNTS_KEY];
        if (others && counted) {
          const text = counted.replace("{n}", others[2]!);
          return others[1] ? `${others[1]} and ${text}` : text;
        }
        // Milliers séparés d'une espace (« 165 000 ») : virgule anglaise (« 165,000 »).
        if (/^\d{1,3}(?:[\u202f\u00a0 ]\d{3})+$/.test(value)) return value.replace(/[\u202f\u00a0 ]/g, ",");
        return /^-?\d+,\d+$/.test(value) ? value.replace(",", ".") : value; // « 3,8 » → « 3.8 »
      });
    }
  }
  return message;
}
